"use client";
import { useMemo } from "react";
import IconDescription from "@/components/IconDescription";
import FormattedPrice from "@/components/ProgramDetailLayout/templates/LegacyDesign/components/FormattedPrice";
import { IconTypes } from "@/components/icons/types";
import type { ProcessedCourse } from "@/common/course/types";
import { PacingType, ProgramType } from "@/common/program/types";
import getTotalEstimatedEffortInHoursPerWeek from "@/lib/program/utils/getTotalEstimatedEffortInHoursPerWeek";
import getProgramPacing from "@/lib/program/utils/getProgramPacing";
import useTranslations from "@/i18n/useTranslations";

interface StatProps {
  iconType: IconTypes;
  title: string | React.ReactNode;
  subtitle: string;
}

function Stat({ iconType, title, subtitle }: StatProps) {
  return (
    <li>
      <IconDescription
        iconType={iconType}
        title={title}
        subtitle={subtitle}
        textColor="text-primary"
      />
    </li>
  );
}

interface ProgramSummaryStatsProps {
  courses: ProcessedCourse[];
  creditValue?: number | null;
  currency?: string;
  discountedPrice?: number;
  duration?: string | null;
  originalPrice?: number;
  programType: ProgramType;
}

function ProgramSummaryStats({
  courses,
  creditValue,
  currency,
  discountedPrice,
  duration,
  originalPrice,
  programType,
}: ProgramSummaryStatsProps) {
  const t = useTranslations("Program");
  const tCourse = useTranslations("Course");
  const pacingType = useMemo(() => getProgramPacing(courses), [courses]);

  const expertInstructionSubtitle = (() => {
    switch (programType) {
      case ProgramType.MICROMASTERS:
        return t("graduateLevelCourses", { count: courses.length });
      case ProgramType.PROFESSIONALCERTIFICATE:
        return t("skillBuildingCourses", { count: courses.length });
      default:
        return t("highQualityCourses", { count: courses.length });
    }
  })();

  return (
    <ul className="list-none flex flex-col gap-4">
      <Stat
        iconType="library-books"
        title={t("expertInstruction")}
        subtitle={expertInstructionSubtitle}
      />
      {pacingType && pacingType !== "mixed" && (
        <Stat
          iconType="person"
          title={
            pacingType === PacingType.SELFPACED
              ? tCourse("selfPaced")
              : tCourse("instructorLed")
          }
          subtitle={
            pacingType === PacingType.SELFPACED
              ? tCourse("progressAtYourOwnSpeed")
              : tCourse("assignmentsHaveDueDates")
          }
        />
      )}
      {duration && (
        <Stat
          iconType="clock"
          title={duration}
          subtitle={`${getTotalEstimatedEffortInHoursPerWeek(courses)}`}
        />
      )}
      {creditValue && (
        <Stat
          iconType="school"
          title={tCourse("academicCredits", { count: creditValue })}
          subtitle={t("buildSkillsAndTranscript")}
        />
      )}
      {discountedPrice && (
        <Stat
          iconType="credit-card"
          title={
            <FormattedPrice
              discountedPrice={discountedPrice}
              originalPrice={originalPrice}
              currency={currency}
            />
          }
          subtitle={t("forFullProgramExperience")}
        />
      )}
    </ul>
  );
}

export default ProgramSummaryStats;
